import { useQuery } from '@tanstack/react-query';
import { fetchServerVersionInfo } from '../../../connections/services/serverInfoService';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { parsePgMajorVersion } from '../../../../core/engines/pgServerVersion';

const FIFTEEN_MINUTES = 15 * 60 * 1000;

// The parser itself lives in `core/engines/pgServerVersion.ts` so the main
// process can share it without importing this React module; re-exported here
// because every existing caller imports it from this path.
export { parsePgMajorVersion };

/**
 * Fetch the PostgreSQL server major version for a connection. Used to
 * gate the declare-on-text JSON path, which requires `IS JSON` (PG 16+).
 *
 * Cached per connection for 15 minutes; the version doesn't change without
 * an explicit reconnect, and the result is small enough that staleness on
 * a long-lived session isn't a concern.
 *
 * Returns `null` for non-PG engines or when fetching/parsing fails — the
 * UI treats that as "unknown" and lets the user proceed (the engine itself
 * will surface a clear error if the version is unsupported).
 */
export function usePgServerMajorVersion(
  connectionId: string | null | undefined,
  database: string | null | undefined,
  engine: DatabaseEngineId | null | undefined,
): number | null {
  const enabled = Boolean(connectionId && engine === 'pgsql');
  const query = useQuery<number | null>({
    queryKey: ['pg-server-major-version', connectionId, database] as const,
    queryFn: async () => {
      if (!connectionId) return null;
      try {
        const info = await fetchServerVersionInfo(connectionId, 'pgsql', database ?? undefined);
        return parsePgMajorVersion(info.version);
      } catch {
        return null;
      }
    },
    enabled,
    staleTime: FIFTEEN_MINUTES,
  });
  return query.data ?? null;
}
